import axios from "axios";
import { makeAutoObservable, runInAction } from "mobx";
import AppConsts from "../libs/AppConsts";
import http from "../services/http";
import { IResultDto } from "../models/ResultDto";
import req from "../services/req";
import { message } from "antd";

type Entry = "Password" | "Forgot" | "Register" | "Reset";

class AuthStore {
  isLoading = false;
  entry: Entry = "Password";
  config?: IAuthUser;
  lookups?: ILookups;
  isMenuExpanded = true;
  countries?: ICountry[];
  cities?: ICity[];
  selectedCountryId: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isGuest() {
    return !localStorage.getItem("token");
  }

  get isLoggedIn() {
    return !!localStorage.getItem("token");
  }

  get isRegister() {
    return this.entry === "Register";
  }

  get isLogin() {
    return this.entry === "Password";
  }

  get isForgot() {
    return this.entry === "Forgot";
  }

  get isReset() {
    return this.entry === "Reset";
  }

  async checkPromoCode(code: string) {
    try {
      const result = await req.get("promocodes/check", { params: { code } });
      return result.data as IPromo;
    } catch (error) {
      console.error(error);
    }
  }

  async login(dto: any) {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await req.post("/auth/login", dto);
      const token = result.data as ILoginDto;
      
      localStorage.setItem("token", token.authorisation.token);
      
      window.location.href = "/";
      
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async onVerify(dto: any) {
    runInAction(() => { this.isLoading = true });
    try {
      await http.post("/auth/register-employee", dto);
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async logout(withReload: boolean = true) {
    runInAction(() => { this.isLoading = true });
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      if (withReload) window.location.reload();
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async refreshAccessToken() {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      const response = await axios.post(`${AppConsts.host}/auth/refresh`, { refreshToken });
      const { access_token, refresh_token } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("refreshToken", refresh_token);
      return access_token;
    } catch (error) {
      localStorage.removeItem("token");
      window.location.reload();
      throw error;
    }
  }

  async register(dto: IRegisterDto) {
    runInAction(() => { this.isLoading = true });
    try {
      await req.post("/auth/register", dto);
      return { email: dto.email, password: dto.password };
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async registerEmployee(dto: any, invitationId: string) {
  runInAction(() => { this.isLoading = true; });
  
  try {
      const result = await req.post("/auth/register-employee", {
        ...dto,
        invitation: invitationId
      });
      
      return result.data;
    } catch (error: any) {
      console.error("Error in registerEmployee:", error);
      
      throw {
        messages: error.response?.data?.messages,
        message: error.response?.data?.message || error.message
      };
    } finally {
      runInAction(() => { this.isLoading = false; });
    }
  }

  async forgotPassword(dto: IForgotDto) {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await req.post("/auth/forgot-password", dto);
      runInAction(() => { this.entry = "Password"; });
      const response = result.data as IResultDto<any>;
      message.success(response?.message);
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("Учетная запись не найдена");
      }
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async resetPassword(dto: IResetDto) {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await req.post("/auth/reset-password", dto);
      runInAction(() => { this.entry = "Reset"; });
      return result.data as IResultDto<any>;
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async getCurrentProfile() {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await http.get("/auth/user");
      runInAction(() => { this.config = result.data.data; });
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async onFetchLookups() {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await http.get(`/auth/register`);
      runInAction(() => { this.lookups = result.data; });
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async fetchCountries() {
    runInAction(() => { this.isLoading = true });
    try {
      const result = await http.get("/countries");
      runInAction(() => { this.countries = result.data.data; });
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }

  async fetchCities(countryId: string) {
    runInAction(() => {
      this.isLoading = true;
      this.selectedCountryId = countryId;
    });
    try {
      const result = await http.get(`/countries/${countryId}/cities`);
      runInAction(() => { this.cities = result.data.data; });
    } finally {
      runInAction(() => { this.isLoading = false });
    }
  }
}

const authStore = new AuthStore();
export default authStore;

interface ILoginDto {
  user: IAuthUser;
  authorisation: {
    token: string;
    type: string;
  }
}

interface IAuthUser {
  id: string;
  email: string;
  emailVerifiedAt?: Date;
  role: {
    name: string;
    label: string;
  };
  profile: {
    id: string;
    firstName: string;
    lastName?: string;
    avatar: string;
  };
  company?: ICompany;
}

interface ICompany {
  city?: ICity;
}

interface IRegisterDto {
  email: string;
  password: string;
  passwordConfirmation: string;
  firstName: string;
  lastName?: string;
  domainIds?: string[];
  promocodes?: string[];
}

interface IForgotDto {
  email: string;
}

interface IResetDto {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

interface ILookups {
  domains: [
    {
      id: string;
      value: string;
      title: string;
    }
  ];
}

interface IPromo {
  status: "success" | "failed";
  message: string;
  data: {
    id: string;
    isActive: boolean;
    name: string;
    code: string;
  };
}

interface ICountry {
  id: string;
  name: string;
  isoCode: string;
}

interface ICity {
  id: string;
  name: string;
  district: string;
  country?: ICountry;
}
